import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';

export class StringUtil {
	public static readonly TAG_REGEX = /\$[A-Za-z_]+[0-9]*\$/g;
	public static readonly FILL_TEMPLATE_DELIMITER = '$';

	public static splitSpaceLessStringForChart(text: string, splitLength: number, useHTMLSeparator = false): string {
		if (!this.hasWhiteSpace(text)) {
			const regex = new RegExp('(.{' + splitLength + '})', 'g');
			if (useHTMLSeparator) {
				return text.replace(regex, '$1</br>');
			} else {
				// DO NOT REMOVE THE SPACE AFTER $1
				return text.replace(regex, '$1 ');
			}
		}
		return text;
	}

	/**
	 * Escapes the HTML special characters of a value so it can safely be interpolated into an HTML string,
	 * such as the markup Highcharts renders for `useHTML` tooltips and labels.
	 */
	public static escapeHtml(value: unknown): string {
		return String(value ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	/**
	 * Escapes a string produced by splitSpaceLessStringForChart(text, length, true), keeping the line break
	 * separators it injects.
	 */
	public static escapeHtmlWithChartLineBreaks(value: unknown): string {
		return StringUtil.escapeHtml(value).replace(/&lt;\/br&gt;/g, '</br>');
	}

	/**
	 * Adds suffix separated by _
	 */
	public static addSuffix(text: string, suffix: string, separator: string = '_') {
		if (suffix) {
			if (text.endsWith(separator)) {
				return text + suffix;
			} else {
				return text + separator + suffix;
			}
		}

		return text;
	}

	public static hasWhiteSpace(str: string): boolean {
		const regex = /\s/g;
		return regex.test(str);
	}

	public static replaceAll(text: string, searchString: string, replacement: string, ignoreCase: boolean = true): string | undefined {
		if (!text || !searchString) {
			return text;
		}

		return text.replace(
			new RegExp(searchString.replace(/([\/\,\!\\\^\$\{\}\[\]\(\)\.\*\+\?\|\<\>\-\&])/g, '\\$&'), ignoreCase ? 'gi' : 'g'),
			typeof replacement === 'string' ? replacement.replace(/\$/g, '$$$$') : replacement
		);
	}

	public static convertStringForRegex(text: string): string {
		return text.replace(/[\/\,\!\\\^\$\{\}\[\]\(\)\.\*\+\?\|\<\>\-\&]/g, '\\$&');
	}

	public static testWithRegex(text: string, pattern: string): boolean {
		const regex = new RegExp(pattern, 'g');
		return regex.test(text);
	}

	/** Split string in order to prepare them for word break
	 * Criteria are as following
	 * 1.(?=[a-z0-9A-Z])[A-Z][a-z0-9\\'\\"]{1,${maxPartLength - 2}}\\s : CamelCase ending with space and not exceeding maximum length
	 * 2.(?=[a-z0-9A-Z])[A-Z][a-z0-9\\'\\"]{1,${maxPartLength - 1}} : Split multiple CamelCase in one word with no spacing and each part not exceeding maximum length
	 * 3.[.\\-_][A-Z0-9\\'\\"]{2,${maxPartLength - 1}} : All UPPER CASE Word started with dot,dash or underscore and not exceeding maximum length
	 * 4.[.\\-_][a-z0-9\\'\\"]{2,${maxPartLength - 1}} : All lower case Word started with dot,dash or underscore and not exceeding maximum length
	 * 5.[a-z0-9A-Z\\'\\"]{0,${maxPartLength-3}}[.]{3}\\s? : Any word ending with "..."
	 * 6.[a-z0-9\\'\\"]{1,${maxPartLength}}\\s? : All lower case word ending with space (normal typing case) and not exceeding maximum length
	 * 7.[A-Z0-9\\'\\"]{1,${maxPartLength}}\\s? : All UPPER CASE word ending with space (normal typing case) and not exceeding maximum length
	 * 8.The rest will be counted as individual character.
	 * @param text original string to split
	 * @param splitLength  maximum length of each sub string
	 * @param stringLengthLimit  hard limit of string length before adding ...
	 */
	public static splitStringByPattern(text: string, splitLength: number, stringLengthLimit: number): string[] {
		const maxPartLength = Math.floor(splitLength / 2);
		let pattern = `(?=[a-z0-9A-Z])[A-Z][a-z0-9\\'\\"]{1,${maxPartLength - 2}}\\s|(?=[a-z0-9A-Z])[A-Z][a-z0-9\\'\\"]{1,${maxPartLength -
			1}}|[.\\-_][A-Z0-9\\'\\"]{2,${maxPartLength - 1}}|[.\\-_][a-z0-9\\'\\"]{2,${maxPartLength - 1}}|[a-z0-9A-Z\\'\\"]{0,${maxPartLength -
			3}}[.]{3}\\s?|[a-z0-9\\'\\"]{1,${maxPartLength}}\\s?|[A-Z0-9\\'\\"]{1,${maxPartLength}}\\s?|.`;

		if (StringUtil.hasWhiteSpace(text)) {
			// normal typing will be left to be handled natively in html or Highcharts
			const nativeTypingPattern = '[^\\s]+\\s?';
			const nativeTypingRegex: RegExp = new RegExp(nativeTypingPattern, 'g');
			const isStringFormatted = (<Array<string>>text.match(nativeTypingRegex)).every(part => part.replace(/[\s]?$/, '').length <= splitLength);
			if (isStringFormatted) {
				pattern = nativeTypingPattern;
			}
		} else {
			// else we do our magic
			if (maxPartLength <= 2) {
				console.warn('Suggested maximum length of string part is less than 2, you better off with word-break');
				return [];
			}
		}
		const regex: RegExp = new RegExp(pattern, 'g');
		// if the text is more than maximum chars long, cut the word off
		if (stringLengthLimit && text && text.length > stringLengthLimit) {
			text = text.substring(0, stringLengthLimit) + '...';
		}
		const parts = text.match(regex);
		return parts;
	}

	/**
	 *
	 * @param parts string parts to joins, use in conjunction with StringUtil.splitStringByPattern()
	 * @param maximumNumberOfCharsPerLine
	 * @param maximumLines limited number of line before adding "..."
	 */
	public static joinStringPartsForChartTitle(parts: string[], maximumNumberOfCharsPerLine: number, maximumLines: number, useHTMLSeparator = false): string {
		let resultString: string = '';
		let currentLine: string = '';
		let lineCount: number = 1;
		parts.forEach(part => {
			if (currentLine.length + part.length > maximumNumberOfCharsPerLine) {
				lineCount++;
				if (lineCount > maximumLines) {
					resultString = resultString.replace(/[\s.]?$/, '...'); // add "..." to the end of result and cut off the rest
					return true;
				}
				currentLine = part; // reset the line
				resultString = resultString.replace(/[\s]?$/, useHTMLSeparator ? '</br>' : ' ') + part; // add space for new line part
			} else {
				currentLine += part;
				resultString += part; // append part normally
			}
		});
		return resultString;
	}

	/**
	 * Method to translate singular word to plural, word to translate must be in ${} format
	 * eg.
	 * pluralizeString("pluralize ${this} ${word}") === "pluralize these words"
	 * @param stringToEvaluate sentence to pluralize, will only pluralize the word in ${} pattern only, and the words should all be in singular
	 * This method does not support revert from plural to singular yet
	 */
	public static delimitedAndPluralizeString(stringToEvaluate: string, pluralize: boolean = true): string {
		const singularWordRegex = /\${[A-Za-z_]+[0-9]*}/g; // Example: ${fund}
		const delimiterRegex = /[{}\$]/g;

		let result = stringToEvaluate;
		const singularWords = stringToEvaluate.match(singularWordRegex);
		if (!isEmpty(singularWords)) {
			singularWords.forEach(word => {
				let delimitedWord = word.replace(delimiterRegex, '');
				if (pluralize) {
					delimitedWord = this.pluralizeWord(delimitedWord);
				}
				result = this.replaceAll(result, word, delimitedWord);
			});
		}
		return result;
	}

	/**
	 * Method to pluralize word
	 * eg.
	 * pluralizeWord('test') === 'tests'
	 * @param word any word to pluralize
	 */
	public static pluralizeWord(word: string): string {
		const specialPluralizeWordsMap = {
			this: 'these',
			that: 'those',
			is: 'are',
			quiz: 'quizzes',
			foot: 'feet',
			goose: 'geese',
			child: 'children',
			man: 'men',
			woman: 'women',
			tooth: 'teeth',
			person: 'people',
			mouse: 'mice',
			louse: 'lice',
			matrix: 'matrices',
			vertex: 'vertices',
			index: 'indices',
			Index: 'Indices',
			a: '',
			an: ''
			// there are a lot more, but I don't think we will use any of those
		};

		const uncountable = ['cash', 'other'];

		const pluralizeMap = {
			'^(ox)$': '$1en',
			'(bus|alias)$': '$1es',
			'(tomat|potat|ech|her|vet)o$': '$1oes',
			'([^aeiouy]|qu)y$': '$1ies',
			'(x|ch|ss|sh)$': '$1es',
			'([^s]+)$': '$1s'
			// this is not all the pattern, feel free to add more if you can think of anything
		};

		if (uncountable.includes(word.toLowerCase())) {
			return word;
		}
		if (!isNil(specialPluralizeWordsMap[word])) {
			return specialPluralizeWordsMap[word];
		} else {
			let result: string = word;
			Object.keys(pluralizeMap).forEach(regex => {
				const pattern = new RegExp(regex, 'i');
				if (pattern.test(result)) {
					result = word.replace(pattern, pluralizeMap[regex]);
				}
			});

			return result;
		}
	}

	/**
	 * Splits the provided string in multiple values if it contains multi-line string value and return an array with all split and trimmed values.
	 * If the provided string does not contain multiple line values, an array with the valueToSplit is returned.
	 * If the provided string in nil or '', empty string is returned.
	 *  
	 * @param valueToSplit
	 * @returns array of split and trimmed values.
	 */
	public static splitLines(valueToSplit: string): string[] {
		let splitLinesArray = [];

		if (valueToSplit) {
			splitLinesArray = valueToSplit.trim().split(/\r\n|\r|\n/);
		}

		return splitLinesArray.map(value => value.trim());
	}

	/**
	 * Looks through a string and replaces tags with values in the params map
	 * Tags are identified by a delimiter character found before and after them e.g. $myTag$
	 * A simplified, non-recursive version of the replacer.resolveDataValues method
	 * 
	 * @param template a string containing tags to be replaced in the format 'this is my string $key$
	 * @param params a map of key => value { key1: val1, key2: val2 }
	 */
	public static fillTemplate(template: string, params: { [tag: string]: string }): string {
		const matches = template.match(StringUtil.TAG_REGEX);
		matches.forEach(match => {
			const rawTag = StringUtil.replaceAll(match, StringUtil.FILL_TEMPLATE_DELIMITER, '');
			template = StringUtil.replaceAll(template, match, params[rawTag]);
		});

		return template;
	}

	/**
	 * Formatter intended for use with components that require a custom benchmark to be substituted in to a DisplayTable when the text
	 * is coming from an extractor (where we do not know the benchmark)
	 * 
	 * It uses the above fillTemplate() method to do this
	 * More properties can be added when we have more use cases
	 * In the event of adding a lot of properties, we should consider dynamically passing in the properties
	 * Must return function() to be able to pass scope to the DisplayTable
	 */
	public static getCustomBenchmarkFormatter(): Function {
		return function (): any {
			return {
				validTemplates: this.formatterTemplates,
				benchmark: this.customBenchmark,
				formatter: function (textToFormat: string): string {
					const customBenchmark = this.benchmark;
					const templates: string[] = this.validTemplates;
					if (customBenchmark && templates.includes(textToFormat)) {
						return StringUtil.fillTemplate(
							textToFormat,
							{ customBenchmark }
						);
					} else {
						return textToFormat;
					}
				}
			};
		};
	}

}
